import { type FlightJourneySegment, TGV_STATION_IATA_CODES } from '@massilia/queries'
import { useMemo } from 'react'

import { formatDuration } from '@/utils/format-duration'
import { formatTime } from '@/utils/format-time'

import { StopsIndicator } from './stops-indicator'

interface FlightCardSegmentProps {
  segment: FlightJourneySegment
  segmentType?: SegmentType
}

// Segment type for different trip modes:
// - 'outbound': outbound segment for one-way or round-trip
// - 'return': return segment for round-trip
// - number: leg index for multi-destination (1, 2, 3, etc.)
type SegmentType = 'outbound' | 'return' | number

export function FlightCardSegment({ segment, segmentType = 'outbound' }: FlightCardSegmentProps) {
  // Helper to get segment label for multi-destination
  const getSegmentLabel = (): null | string => {
    if (typeof segmentType === 'number') {
      return `Segment ${segmentType}`
    }
    return null // No label for simple outbound/return in current design
  }

  const [departureIsTgv, arrivalIsTgv] = useMemo(() => {
    return [
      TGV_STATION_IATA_CODES.has(segment.departureAirport),
      TGV_STATION_IATA_CODES.has(segment.arrivalAirport),
    ]
  }, [segment])

  const segmentLabel = getSegmentLabel()

  // Check if arrival is on a different day than departure
  const isNextDay = segment.arrivalDate !== segment.departureDate

  return (
    <div className="flex flex-col gap-1">
      {/* Segment label for multi-destination */}
      {segmentLabel && (
        <div className="text-[10px] font-medium tracking-wide text-gray-500 uppercase">
          {segmentLabel}
        </div>
      )}

      {/*
        `1fr auto 1fr` : la pastille occupe sa largeur naturelle et les deux
        colonnes latérales se partagent le reste à parts égales, ce qui la place
        exactement au centre du bloc — donc à la même verticale d'une ligne à
        l'autre, que l'aller affiche « Direct » et le retour « 2 escales » ou
        l'inverse. Des colonnes en `auto` dimensionnaient chaque ligne sur son
        propre contenu (le suffixe « +1 » suffisait à décaler la pastille) ; des
        largeurs fixes débordaient la colonne gauche dès qu'un filtre latéral la
        réduisait. Le nom de ville n'apparaît qu'à `xl` : en deçà, la colonne ne
        peut pas le tenir sans le replier sur trois lignes.
      */}
      <div
        data-segment-row
        className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-3">
        {/* Departure */}
        <div className="flex flex-col">
          <span className="text-sm leading-tight font-bold whitespace-nowrap text-gray-900 md:text-base">
            {formatTime(segment.departureTime)}
          </span>
          <span className="flex flex-wrap items-center gap-1 text-[11px] leading-tight text-gray-500 md:text-xs">
            <span className="xl:hidden">{segment.departureAirport}</span>
            <span className="hidden xl:inline">
              {segment.departureCity} ({segment.departureAirport})
            </span>
            {departureIsTgv && (
              <img alt="TGV+Air" className="h-3.5 w-auto md:h-4" src="/logos/logo-tgv-air.png" />
            )}
          </span>
        </div>

        {/* Flight path: separator - indicator - separator */}
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-[10px] leading-none text-gray-500 md:text-xs">
            {formatDuration(segment.totalDuration)}
          </span>
          {/*
            Le trait était rendu `opacity-0` sur mobile : il occupait la grille sans
            rien montrer. Le rendre visible ne coûte pas un pixel de hauteur et rend
            la lecture aller/retour immédiate.
          */}
          <div className="flex w-full items-center">
            <div className="h-px flex-1 bg-gray-200" />
            <div className="mx-1.5 md:mx-3">
              <StopsIndicator legs={segment.legs} stops={segment.stops} variant="badge" />
            </div>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        </div>

        {/* Arrival */}
        <div className="flex flex-col items-end">
          <span className="text-end text-sm leading-tight font-bold whitespace-nowrap text-gray-900 md:text-base">
            {formatTime(segment.arrivalTime)}
            {isNextDay && <span className="ml-0.5 text-[10px] font-bold text-orange-600">+1</span>}
          </span>
          <span className="flex flex-wrap items-center justify-end gap-1 text-end text-[11px] leading-tight text-gray-500 md:text-xs">
            {arrivalIsTgv && (
              <img alt="TGV+Air" className="h-3.5 w-auto md:h-4" src="/logos/logo-tgv-air.png" />
            )}
            <span className="xl:hidden">{segment.arrivalAirport}</span>
            <span className="hidden xl:inline">
              {segment.arrivalCity} ({segment.arrivalAirport})
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}
